import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { formatDMY } from "@/lib/date";
import { pickInitialSeasonId, saveSeasonPref } from "@/lib/seasonPref";
import { Download, Search } from "lucide-react";

const PLACEMENTS = ["", "FIRST", "SECOND", "THIRD", "HIGHLY_COMMENDED", "COMMENDED"];

export default function Results() {
  const [seasons, setSeasons] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ season_id: "", competition_id: "", placement: "" });
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "competition_date", dir: -1 });
  const [page, setPage] = useState(1);
  const pageSize = 25;

  useEffect(() => {
    (async () => {
      const { data: s } = await api.get("/seasons");
      setSeasons(s);
      const initial = pickInitialSeasonId(s);
      if (initial) setFilters((f) => ({ ...f, season_id: initial }));
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const params = {};
      if (filters.season_id) params.season_id = filters.season_id;
      const { data } = await api.get("/competitions", { params });
      setCompetitions(data);
    })();
  }, [filters.season_id]);

  const loadEntries = async () => {
    const params = {};
    if (filters.season_id) params.season_id = filters.season_id;
    if (filters.competition_id) params.competition_id = filters.competition_id;
    if (filters.placement) params.placement = filters.placement;
    if (q) params.q = q;
    const { data } = await api.get("/entries", { params });
    setRows(data);
    setPage(1);
  };

  useEffect(() => {
    const t = setTimeout(loadEntries, q ? 220 : 0);
    return () => clearTimeout(t);
  }, [filters, q]);

  const cmap = useMemo(() => {
    const m = {};
    competitions.forEach((c) => { m[c.id] = c; });
    return m;
  }, [competitions]);

  const sorted = useMemo(() => {
    const arr = [...rows].map((r) => ({
      ...r,
      competition_name: cmap[r.competition_id]?.competition_name || "",
      competition_date: cmap[r.competition_id]?.competition_date || "",
      category: cmap[r.competition_id]?.category || "",
      competition_type: cmap[r.competition_id]?.competition_type || "",
    }));
    arr.sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av == null) return 1; if (bv == null) return -1;
      if (av < bv) return -1 * sort.dir;
      if (av > bv) return 1 * sort.dir;
      return 0;
    });
    return arr;
  }, [rows, sort, cmap]);

  const paged = sorted.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (filters.season_id) params.set("season_id", filters.season_id);
    if (filters.competition_id) params.set("competition_id", filters.competition_id);
    if (filters.placement) params.set("placement", filters.placement);
    const token = localStorage.getItem("ccs_token");
    if (token) params.set("__t", "1"); // signal; cookie used too
    const url = `${API}/export/entries.csv?${params.toString()}`;
    // Open with credentials by fetching blob (so auth header attaches)
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "results.csv";
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="ccs-label mb-2">Archive</div>
          <h1 className="text-4xl font-semibold tracking-tight">Results</h1>
        </div>
        <button onClick={exportCsv} className="ccs-btn" data-testid="export-csv-button">
          <Download size={16} /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <select
          className="ccs-input"
          value={filters.season_id}
          onChange={(e) => { setFilters({ ...filters, season_id: e.target.value, competition_id: "" }); saveSeasonPref(e.target.value); }}
          data-testid="results-season-filter"
        >
          <option value="">All seasons</option>
          {seasons.map((s) => <option key={s.id} value={s.id}>{s.season_name}</option>)}
        </select>
        <select
          className="ccs-input"
          value={filters.competition_id}
          onChange={(e) => setFilters({ ...filters, competition_id: e.target.value })}
        >
          <option value="">All competitions</option>
          {competitions.map((c) => <option key={c.id} value={c.id}>{c.competition_name}</option>)}
        </select>
        <select
          className="ccs-input"
          value={filters.placement}
          onChange={(e) => setFilters({ ...filters, placement: e.target.value })}
        >
          <option value="">Any placement</option>
          {PLACEMENTS.filter(Boolean).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            className="ccs-input pl-10"
            placeholder="Search title or photographer"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="results-search"
          />
        </div>
      </div>

      <div className="ccs-card p-0 overflow-x-auto">
        <table className="ccs-table" data-testid="results-table">
          <thead>
            <tr>
              <Th onClick={() => toggleSort("photographer_code")}>I.D.</Th>
              <Th onClick={() => toggleSort("photographer_name")}>Photographer</Th>
              <Th onClick={() => toggleSort("image_title")}>Title</Th>
              <Th onClick={() => toggleSort("score")}>Score</Th>
              <Th onClick={() => toggleSort("placement")}>Place</Th>
              <Th onClick={() => toggleSort("competition_name")}>Competition</Th>
              <Th onClick={() => toggleSort("competition_date")}>Date</Th>
              <Th onClick={() => toggleSort("category")}>Category</Th>
            </tr>
          </thead>
          <tbody>
            {paged.map((r) => (
              <tr key={r.id}>
                <td className="mono text-amber-400">{r.photographer_code}</td>
                <td>{r.photographer_name}</td>
                <td>{r.image_title}</td>
                <td className="mono text-right" style={{ paddingRight: 32 }}>{r.score}</td>
                <td>
                  <span className={`placement-badge placement-${r.placement}`}>
                    {r.placement === "NONE" ? "—" : r.placement.replace("_", " ")}
                  </span>
                </td>
                <td>{r.competition_name}</td>
                <td className="mono muted-text">{formatDMY(r.competition_date)}</td>
                <td><span className="tag">{r.category}</span></td>
              </tr>
            ))}
            {paged.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 muted-text">No results.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="muted-text text-xs">
          Showing {paged.length ? (page - 1) * pageSize + 1 : 0}–
          {Math.min(page * pageSize, sorted.length)} of {sorted.length}
        </div>
        <div className="flex items-center gap-2">
          <button className="ccs-btn-ghost text-sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
            Prev
          </button>
          <div className="mono muted-text text-sm">{page} / {totalPages}</div>
          <button
            className="ccs-btn-ghost text-sm"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function Th({ onClick, children }) {
  return (
    <th onClick={onClick} className="cursor-pointer select-none hover:text-white">
      {children}
    </th>
  );
}
